import { css } from '@emotion/react'
import {
  Button,
  Cell,
  Grid,
  Heading,
  HFlow,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { Box } from 'components/Box'
import { useLoadAtestadoByIdLazyQuery } from 'graphql/hooks.generated'
import { TipoAtestadoEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import { formatDayMonthYear } from 'util/date/formatDate'
import { ProfissionalAtendimento } from 'view/atendimentos/types/AtendimentoProfissionalModel'

import {
  ASSINATURA_CIDADAO_TEXT,
  atestadoComCID10Text,
  CID10_DESCRICAO_ATESTADO,
  tituloAtestado,
} from '../utils-atestado'
import { RegistroLista } from './AtestadosTable'

export interface AtestadosVisualizacaoModalProps {
  registro: RegistroLista
  descricaoAtestadoRecente?: string
  nomeCidadao: string
  nomeSocialCidadao: string
  hasPermissionImprimirAtestado: boolean
  handlePrint(): void
  handleModalClose(): void
}

export default function AtestadosVisualizacaoModal(props: Readonly<AtestadosVisualizacaoModalProps>) {
  const {
    registro,
    descricaoAtestadoRecente,
    hasPermissionImprimirAtestado,
    handlePrint,
    handleModalClose,
    nomeCidadao,
    nomeSocialCidadao,
  } = props
  const [executeQuery, { data }] = useLoadAtestadoByIdLazyQuery()
  const theme = useTheme()
  const classes = createStyles(theme)

  useMemo(() => {
    if (!registro.recente) executeQuery({ variables: { id: registro.id } })
  }, [executeQuery, registro.id, registro.recente])

  const tipoAtestado = registro.tipo
  const tituloModal =
    tipoAtestado === TipoAtestadoEnum.LICENCA_MATERNIDADE ? 'Visualizar licença-maternidade' : 'Visualizar atestado'
  const descricao = registro.recente ? descricaoAtestadoRecente : data?.atestado.descricao
  const municipioRegistro = registro?.atendimentoProfissional?.lotacao.municipio
  const dataAtendimentoRegistro = registro?.atendimentoProfissional?.iniciadoEm
  const nomeCboRegistro = registro?.atendimentoProfissional?.lotacao.cbo.nome
  const profissionalRegistro = registro?.atendimentoProfissional?.lotacao.profissional as ProfissionalAtendimento
  const dataPorExtenso = formatDayMonthYear(dataAtendimentoRegistro)
  const showAssinaturaCidadao = tipoAtestado && descricao?.includes(CID10_DESCRICAO_ATESTADO)
  const nomeCidadaoFormatted = !nomeSocialCidadao ? nomeCidadao : `${nomeSocialCidadao} - (${nomeCidadao})`

  return (
    <Modal style={classes.modal} onClose={handleModalClose} open>
      <ModalHeader title={tituloModal} />
      <ModalBody>
        <Box>
          {tipoAtestado && (
            <VFlow style={classes.header}>
              <Heading level={3}>{tituloAtestado(tipoAtestado)}</Heading>
            </VFlow>
          )}
          <Grid>
            <Cell size={12}>
              <VFlow style={classes.body}>
                <Text dangerouslySetInnerHTML={{ __html: descricao }} style={classes.breakDescription} />
              </VFlow>
            </Cell>
            <Cell size={3} />
          </Grid>
          <VFlow style={classes.center} vSpacing={0}>
            {`${municipioRegistro?.nome.toUpperCase()} - ${municipioRegistro?.uf.sigla},  ${dataPorExtenso}`}
            <VFlow style={classes.signature} vSpacing={0}>
              <Text fontWeight='bold'>
                {profissionalRegistro.nome}
                {profissionalRegistro.conselhoClasse &&
                  ` - ${profissionalRegistro.conselhoClasse.sigla} - ${profissionalRegistro.ufEmissoraConselhoClasse.sigla} ${profissionalRegistro.numeroConselhoClasse}`}
              </Text>
              {profissionalRegistro && <Text>{nomeCboRegistro}</Text>}
            </VFlow>
          </VFlow>
          {showAssinaturaCidadao && (
            <VFlow style={[classes.center, classes.body]} vSpacing={0}>
              <Text>
                {atestadoComCID10Text({
                  nomeCidadao: nomeCidadaoFormatted,
                  nomeProfissional: profissionalRegistro.nome,
                })}
              </Text>
              <VFlow style={classes.signature} vSpacing={0}>
                <Text fontWeight='bold'>{ASSINATURA_CIDADAO_TEXT}</Text>
              </VFlow>
            </VFlow>
          )}
        </Box>
      </ModalBody>
      {hasPermissionImprimirAtestado && (
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Tooltip text='Imprimir'>
              <Button size='medium' kind='primary' onClick={handlePrint}>
                <Icon icon='printerOutline' style={classes.icon} />
                Imprimir
              </Button>
            </Tooltip>
          </HFlow>
        </ModalFooter>
      )}
    </Modal>
  )
}

const createStyles = (theme: Theme) => ({
  signature: css`
    border-top: solid;
    border-top-width: 1px;
    border-top-color: ${theme.pallete.gray.c30};
    align-items: center;
    margin-top: 4rem;
    margin-bottom: 1.5rem;
    font-size: 13px;
  `,
  center: css`
    justify-content: center;
    align-items: center;
  `,
  body: css`
    text-align: justify;
    margin: 1rem 2rem;
  `,
  header: css`
    margin: 1.5rem 0.5rem;
    text-align: center;
  `,
  modal: css`
    width: 41rem;
  `,
  icon: css`
    margin-right: 0.75rem;
  `,
  breakDescription: css`
    word-break: break-word;
  `,
})
